/**
 * TCX-6485 follow-up: a brand-new agent's call data can lag the roster by up to ~60s while the
 * data source's cache warms up (Flow1/Flow2 backend timing), so the very first fetch right after
 * a roster/filter change can legitimately return fewer rows than the agent scope requested.
 *
 * Re-issues `fetchOnce` until the returned count reaches `expectedCount`, or `maxAttempts` is
 * exhausted. Condition-based, not a blind fixed-delay retry: it converges the moment the cache is
 * actually ready instead of guessing a delay, and it still gives up cleanly (after `maxAttempts`)
 * for a scope that legitimately has zero data in the selected range.
 *
 * `expectedCount` of `null`/`undefined`/`0` means "no known expected count" — the first result is
 * returned as-is, no retry.
 */
export async function fetchUntilCountMatches<T>(
  fetchOnce: () => Promise<T>,
  getCount: (result: T) => number,
  expectedCount: number | null | undefined,
  options: { maxAttempts?: number; intervalMs?: number; signal?: AbortSignal } = {}
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? 3;
  const intervalMs = options.intervalMs ?? 10000;

  let result = await fetchOnce();
  if (!expectedCount) return result;

  for (let attempt = 1; attempt < maxAttempts && getCount(result) < expectedCount; attempt++) {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(resolve, intervalMs);
      const onAbort = () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      };
      if (options.signal?.aborted) onAbort();
      else options.signal?.addEventListener("abort", onAbort, { once: true });
    });
    result = await fetchOnce();
  }
  return result;
}
